import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_ANALYTICS_API_URL;

// ─── Filtros y Request común ──────────────────────────────────────────────────

export interface ReportDateRange {
  start: string;
  end: string;
}

export interface ReportFilters {
  dateRange: ReportDateRange;
  userId?: string | null;
  pipelineId?: string | null;
  contactId?: number | null;
  clientCompanyId?: number | null;
}

export interface AnalyticsConfig {
  stalledDays: number;
  retentionMonths: number;
}

/** Body unificado que reciben TODOS los endpoints de analytics */
export interface AnalyticsRequest {
  slug: string;
  filters: ReportFilters;
  config: AnalyticsConfig;
}

// ─── Meta (viene en cada respuesta) ───────────────────────────────────────────

export interface MetaScope {
  isPipelineView: boolean;
  isUserView: boolean;
  pipelineName: string | null;
  userName: string | null;
  userSalesTarget: number | null;
}

export interface MetaPipeline {
  id: string;
  name: string;
  income_objective: number;
}

export interface AnalyticsMeta {
  effectiveTarget: number;
  scope: MetaScope;
  pipelines: MetaPipeline[];
}

export interface LeadVolumeMetric {
  metric: "leads_volume";
  value: number;
  label: string;
  description: string;
}

export interface LeadResponseTimeMetric {
  metric: "lead_response_time";
  value: number;
  unit: "hours";
  label: string;
  description: string;
}

export interface ContactabilityMetric {
  metric: "contactability_rate";
  value: number;
  unit: "%";
  label: string;
  description: string;
  details: {
    total: number;
    contacted: number;
  };
}

export interface QualificationMetric {
  metric: "qualification_rate";
  value: number;
  unit: "%";
  label: string;
  description: string;
  details: {
    total: number;
    qualified: number;
  };
}

export interface LeadConversionMetric {
  metric: "lead_conversion_rate";
  value: number;
  unit: "%";
  label: string;
  description: string;
  details: {
    total: number;
    won: number;
  };
}

export interface LeadsMetrics {
  volume: LeadVolumeMetric;
  responseTime: LeadResponseTimeMetric;
  contactability: ContactabilityMetric;
  qualification: QualificationMetric;
  conversion: LeadConversionMetric;
}

export interface LeadsMetricsResponse {
  category: "leads";
  metrics: LeadsMetrics;
}

// ─── Generics de respuesta API ────────────────────────────────────────────────

export interface ApiResponse<T> {
  statusCode: number;
  data: {
    data: T & { meta: AnalyticsMeta };
  };
}

export interface PipelineValueMetric {
  metric: "pipeline_value";
  value: number;
  unit: "currency";
  label: string;
  description: string;
}

export interface AverageSizeMetric {
  metric: "average_opportunity_size";
  value: number;
  unit: "currency";
  label: string;
  description: string;
  details: {
    count: number;
    total: number;
  };
}

export interface PipelineCoverageMetric {
  metric: "pipeline_coverage";
  value: number;
  unit: "%";
  label: string;
  description: string;
  details: {
    pipelineValue: number;
    salesTarget: number;
    gap: number;
  };
}

export interface StageConversionData {
  from_stage: string;
  to_stage: string;
  rate: number;
  entered: number;
  advanced: number;
}

export interface StageConversionMetric {
  metric: "stage_conversion_rate";
  value: StageConversionData[];
  label: string;
  description: string;
}

export interface ForecastAccuracyMetric {
  metric: "forecast_accuracy";
  value: number;
  unit: "%";
  label: string;
  description: string;
}

export interface WinRateMetric {
  metric: "win_rate";
  value: number;
  unit: "%";
  label: string;
  description: string;
  details: {
    totalClosed: number;
    won: number;
    lost: number;
  };
}

export interface PipelineMetrics {
  pipelineValue: PipelineValueMetric;
  averageSize: AverageSizeMetric;
  coverage: PipelineCoverageMetric;
  stageConversion: StageConversionMetric;
  forecastAccuracy: ForecastAccuracyMetric;
  winRate: WinRateMetric;
}

export interface PipelineMetricsResponse {
  category: "pipeline";
  metrics: PipelineMetrics;
}

// ─── TIME ─────────────────────────────────────────────────────────────────────

export interface CycleDurationMetric {
  metric: "sales_cycle_duration";
  value: number;
  unit: "days";
  label: string;
  description: string;
  details: {
    count: number;
    shortest: number;
    longest: number;
  };
}

export interface StalledOpportunity {
  id: string;
  name: string;
  stage: string;
  total: number;
  days_stalled: number;
}

export interface StalledOpportunitiesMetric {
  metric: "stalled_opportunities";
  value: number;
  unit: "count";
  label: string;
  description: string;
  details: {
    stalledDays: number;
    opportunities: StalledOpportunity[];
    totalValue: number;
  };
}

export interface TimePerStageData {
  stage_name: string;
  average_days: number;
  count: number;
  min_days: number;
  max_days: number;
}

export interface TimePerStageMetric {
  metric: "average_time_per_stage";
  value: TimePerStageData[];
  unit: "days";
  label: string;
  description: string;
}

export interface TimeEfficiencyMetrics {
  cycleDuration: CycleDurationMetric;
  stalledOpps: StalledOpportunitiesMetric;
  timePerStage: TimePerStageMetric;
}

export interface TimeEfficiencyResponse {
  category: "time";
  metrics: TimeEfficiencyMetrics;
}

// ─── REVENUE ──────────────────────────────────────────────────────────────────

export interface TotalRevenueMetric {
  metric: "total_revenue";
  value: number;
  unit: "currency";
  label: string;
  description: string;
}

export interface AverageTicketMetric {
  metric: "average_ticket";
  value: number;
  unit: "currency";
  label: string;
  description: string;
  details: {
    count: number;
    total: number;
  };
}

export interface RevenueForecastMetric {
  metric: "revenue_forecast";
  value: number;
  unit: "currency";
  label: string;
  description: string;
  details: {
    opportunities_count: number;
    total_potential: number;
  };
}

export interface GoalProgressMetric {
  metric: "goal_progress";
  value: number;
  unit: "%";
  label: string;
  description: string;
  details: {
    revenue: number;
    salesTarget: number;
    pipelineGoal: number;
    targetProgress: number;
    pipelineProgress: number;
    remaining: number;
    pipelineRemaining: number;
  };
}

export interface RevenueMetrics {
  totalRevenue: TotalRevenueMetric;
  averageTicket: AverageTicketMetric;
  forecast: RevenueForecastMetric;
  goalProgress: GoalProgressMetric;
}

export interface RevenueMetricsResponse {
  category: "revenue";
  metrics: RevenueMetrics;
}

// ─── ACTIVITY ─────────────────────────────────────────────────────────────────

export interface ActivitiesPerSellerMetric {
  metric: "activities_per_seller";
  value: number;
  unit: "count";
  label: string;
  description: string;
}

export interface ActivityRatioMetric {
  metric: "activity_opportunity_ratio";
  value: number;
  unit: "ratio";
  label: string;
  description: string;
  details: {
    total_activities: number;
    total_opportunities: number;
  };
}

export interface ActivityMetrics {
  activitiesPerSeller: ActivitiesPerSellerMetric;
  activityRatio: ActivityRatioMetric;
}

export interface ActivityMetricsResponse {
  category: "activity";
  metrics: ActivityMetrics;
}

export interface CustomerRetentionMetric {
  metric: "customer_retention";
  value: number;
  unit: "%";
  label: string;
  description: string;
  details: {
    total_clients: number;
    repeat_clients: number;
    new_clients: number;
  };
}

export interface ChurnRateMetric {
  metric: "churn_rate";
  value: number;
  unit: "%";
  label: string;
  description: string;
  details: {
    retention_rate: number;
    total_clients: number;
    repeat_clients: number;
    new_clients: number;
  };
}

export interface CustomerLifetimeValueMetric {
  metric: "customer_lifetime_value";
  value: number;
  unit: "currency";
  label: string;
  description: string;
  details: {
    average_ticket: number;
    purchase_frequency: number;
    retention_months: number;
    total_clients: number;
  };
}

export interface CustomerMetrics {
  retention: CustomerRetentionMetric;
  churn: ChurnRateMetric;
  clv: CustomerLifetimeValueMetric;
}

export interface CustomerMetricsResponse {
  category: "customer";
  metrics: CustomerMetrics;
}

// ─── CHARTS ───────────────────────────────────────────────────────────────────

export interface LossReasonItem {
  reason: string;
  count: number;
  percentage: number;
  value: number;
}

export interface LossReasonsChart {
  label: string;
  data: LossReasonItem[];
  summary: {
    total_lost: number;
    total_value_lost: number;
  };
}

export interface PipelineByStageItem {
  stage_name: string;
  count: number;
  value: number;
}

export interface PipelineByStageChart {
  label: string;
  data: PipelineByStageItem[];
  summary: {
    total_opportunities: number;
    total_value: number;
  };
}

export interface SalesTrendItem {
  month: string;
  value: number;
  count: number;
}

export interface SalesTrendChart {
  label: string;
  data: SalesTrendItem[];
}

export interface ChartsData {
  lossReasons: LossReasonsChart;
  pipelineByStage: PipelineByStageChart;
  salesTrend: SalesTrendChart;
}

export interface ChartsResponse {
  category: "charts";
  charts: ChartsData;
}

// ─── Tipo unificado (ensamblado en el front) ──────────────────────────────────

export interface UnifiedAnalyticsData {
  meta: AnalyticsMeta;
  leads: LeadsMetricsResponse | null;
  pipeline: PipelineMetricsResponse | null;
  time: TimeEfficiencyResponse | null;
  revenue: RevenueMetricsResponse | null;
  activity: ActivityMetricsResponse | null;
  customer: CustomerMetricsResponse | null;
  charts: ChartsResponse | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const buildBody = (request: AnalyticsRequest) => ({
  slug: request.slug,
  filters: request.filters,
  config: request.config,
});

// ─── Servicio ─────────────────────────────────────────────────────────────────

export const reportsService = {
  getLeadsMetrics: async (
    request: AnalyticsRequest,
  ): Promise<{ data: LeadsMetricsResponse; meta: AnalyticsMeta }> => {
    const response = await axios.post<ApiResponse<LeadsMetricsResponse>>(
      `${API_BASE_URL}/analytics/${request.slug}/leads`,
      buildBody(request),
      { headers: authHeaders() },
    );
    const payload = response.data.data.data;
    const { meta, ...rest } = payload;
    return { data: rest as LeadsMetricsResponse, meta };
  },

  getPipelineMetrics: async (
    request: AnalyticsRequest,
  ): Promise<{ data: PipelineMetricsResponse; meta: AnalyticsMeta }> => {
    const response = await axios.post<ApiResponse<PipelineMetricsResponse>>(
      `${API_BASE_URL}/analytics/${request.slug}/pipeline`,
      buildBody(request),
      { headers: authHeaders() },
    );
    const payload = response.data.data.data;
    const { meta, ...rest } = payload;
    return { data: rest as PipelineMetricsResponse, meta };
  },

  getTimeEfficiencyMetrics: async (
    request: AnalyticsRequest,
  ): Promise<{ data: TimeEfficiencyResponse; meta: AnalyticsMeta }> => {
    const response = await axios.post<ApiResponse<TimeEfficiencyResponse>>(
      `${API_BASE_URL}/analytics/${request.slug}/time`,
      buildBody(request),
      { headers: authHeaders() },
    );
    const payload = response.data.data.data;
    const { meta, ...rest } = payload;
    return { data: rest as TimeEfficiencyResponse, meta };
  },

  getRevenueMetrics: async (
    request: AnalyticsRequest,
  ): Promise<{ data: RevenueMetricsResponse; meta: AnalyticsMeta }> => {
    const response = await axios.post<ApiResponse<RevenueMetricsResponse>>(
      `${API_BASE_URL}/analytics/${request.slug}/revenue`,
      buildBody(request),
      { headers: authHeaders() },
    );
    const payload = response.data.data.data;
    const { meta, ...rest } = payload;
    return { data: rest as RevenueMetricsResponse, meta };
  },

  getActivityMetrics: async (
    request: AnalyticsRequest,
  ): Promise<{ data: ActivityMetricsResponse; meta: AnalyticsMeta }> => {
    const response = await axios.post<ApiResponse<ActivityMetricsResponse>>(
      `${API_BASE_URL}/analytics/${request.slug}/activity`,
      buildBody(request),
      { headers: authHeaders() },
    );
    const payload = response.data.data.data;
    const { meta, ...rest } = payload;
    return { data: rest as ActivityMetricsResponse, meta };
  },

  getCustomerMetrics: async (
    request: AnalyticsRequest,
  ): Promise<{ data: CustomerMetricsResponse; meta: AnalyticsMeta }> => {
    const response = await axios.post<ApiResponse<CustomerMetricsResponse>>(
      `${API_BASE_URL}/analytics/${request.slug}/customer`,
      buildBody(request),
      { headers: authHeaders() },
    );
    const payload = response.data.data.data;
    const { meta, ...rest } = payload;
    return { data: rest as CustomerMetricsResponse, meta };
  },

  getChartsMetrics: async (
    request: AnalyticsRequest,
  ): Promise<{ data: ChartsResponse; meta: AnalyticsMeta }> => {
    const response = await axios.post<ApiResponse<ChartsResponse>>(
      `${API_BASE_URL}/analytics/${request.slug}/charts`,
      buildBody(request),
      { headers: authHeaders() },
    );
    const payload = response.data.data.data;
    const { meta, ...rest } = payload;
    return { data: rest as ChartsResponse, meta };
  },

  getDefaultFilters: (): ReportFilters => {
    const currentYear = new Date().getFullYear();
    return {
      dateRange: {
        start: `${currentYear}-01-01`,
        end: `${currentYear}-12-31`,
      },
      userId: null,
      pipelineId: null,
      contactId: null,
      clientCompanyId: null,
    };
  },
};
